import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { EntityAttachmentsItem } from 'src/app/api/models';
import { AllegatiService, MediaService } from 'src/app/api/services';
import { downloadFile } from 'src/app/utils/utils';
import { AttachmentType } from '../modal-upload/modal-upload.component';

/**
 * Elenco degli allegati di un'entità in un pannello laterale a scomparsa: una linguetta fissa al
 * bordo destro della pagina, normalmente chiusa, che al clic fa scorrere il pannello verso sinistra.
 * La pagina resta utilizzabile mentre il pannello è aperto.
 */
@Component({
  selector: 'app-attachments-drawer',
  templateUrl: './attachments-drawer.component.html',
  styleUrls: ['./attachments-drawer.component.scss'],
})
export class AttachmentsDrawerComponent implements OnChanges {
  @Input({ required: true }) entity!: AttachmentType;
  @Input({ required: true }) entityId!: number;
  /** Mostra il pulsante per aggiungere/eliminare allegati: l'azione la esegue il componente padre. */
  @Input() canManage = false;
  @Output() manage = new EventEmitter<void>();

  visible = false;
  loading = false;
  loadFailed = false;
  attachments: EntityAttachmentsItem[] = [];
  downloadingId?: number;

  constructor(
    private allegatiService: AllegatiService,
    private mediaService: MediaService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['entity'] || changes['entityId']) {
      this.load();
    }
  }

  /** Ricarica l'elenco (es. dopo che il padre ha chiuso la finestra di caricamento). */
  load() {
    if (!this.entityId) return;
    this.loading = true;
    this.loadFailed = false;
    this.allegatiService
      .attachmentsControllerGetAllAttachments({ id: this.entityId, entity: this.entity })
      .subscribe({
        next: res => {
          this.attachments = res.data?.list ?? [];
          this.loading = false;
        },
        error: () => {
          this.attachments = [];
          this.loadFailed = true;
          this.loading = false;
        },
      });
  }

  toggle() {
    this.visible = !this.visible;
    // all'apertura si riallinea l'elenco: potrebbero esserci allegati caricati nel frattempo
    if (this.visible) this.load();
  }

  close() {
    this.visible = false;
  }

  // Esc chiude il pannello, come ci si aspetta da un pannello laterale
  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.visible) this.close();
  }

  iconFor(item: EntityAttachmentsItem): string {
    const mime = item.media.mime ?? '';
    const ext = (item.media.extension ?? '').toLowerCase().replace('.', '');
    if (mime === 'application/pdf' || ext === 'pdf') return 'pi pi-file-pdf';
    if (mime.startsWith('image/')) return 'pi pi-image';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'pi pi-file-excel';
    if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) return 'pi pi-file-word';
    return 'pi pi-file';
  }

  download(item: EntityAttachmentsItem) {
    if (this.downloadingId) return;
    this.downloadingId = item.media.id;
    this.mediaService.mediaControllerGetMedia$Response({ path: item.media.path }).subscribe({
      next: response => {
        downloadFile(response.body, item.media.original_name);
        this.downloadingId = undefined;
      },
      error: () => {
        this.downloadingId = undefined;
        this.messageService.add({
          severity: 'error',
          summary: 'Allegati',
          detail: 'Si è verificato un errore durante il download del file.',
        });
      },
    });
  }

  trackById(_: number, item: EntityAttachmentsItem) {
    return item.media.id;
  }
}
